/* eslint react-hooks/exhaustive-deps: "warn" */

import { ChangeEvent, FormEvent, KeyboardEvent } from 'react';

import cn from 'classnames';

import { Button } from 'ui/atoms/Button/Button';
import { Icon } from 'ui/atoms/Icon/Icon';
import { Spinner } from 'ui/atoms/Spinner/Spinner';
import inputStyles from 'ui/atoms/Input/Input.module.scss';

import type { InputFormProps } from './InputForm.types';

const buttonSizeMap = {
  sm: 'lg',
  md: 'xl',
  lg: 'xxl',
  xl: 'xxl',
} as const;

const sizeClassMap = {
  sm: inputStyles.sizeSm,
  md: inputStyles.sizeMd,
  lg: inputStyles.sizeLg,
  xl: inputStyles.sizeXl,
};

export const InputForm = ({
  value = '',
  onChange,
  placeholder,
  text,
  name,
  disabled = false,
  icon,
  buttonLabel,
  buttonRoute,
  buttonHref,
  onButtonClick,
  loading,
  size = 'lg',
  theme = 'light',
  error,
  onSubmit,
  ariaLabel = 'Enter your email',
  dataTestId,
  fullWidth = false,
  noBorder = false,
  className,
  variant = 'default',
  rows = 3,
}: InputFormProps) => {
  const onFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit?.(value);
    onButtonClick?.();
  };

  if (variant === 'box') {
    const onTextareaChange = ({ target }: ChangeEvent<HTMLTextAreaElement>) => onChange(target.value);

    const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onSubmit?.(value);
        onButtonClick?.();
      }
    };

    return (
      <form
        className={cn(inputStyles.boxForm, disabled && inputStyles.isDisabled, className)}
        onSubmit={onFormSubmit}
        noValidate
      >
        <textarea
          data-test={dataTestId}
          aria-label={ariaLabel}
          className={inputStyles.boxTextarea}
          placeholder={text || placeholder}
          onChange={onTextareaChange}
          onKeyDown={onKeyDown}
          disabled={disabled}
          value={value}
          name={name}
          rows={rows}
        />

        <div className={inputStyles.boxFooter}>
          <button type="submit" className={inputStyles.boxSendButton} disabled={disabled || loading} aria-label="Send">
            {loading ? <Spinner size={16} /> : <Icon type="arrow-15" />}
          </button>
        </div>
      </form>
    );
  }

  const onChangeHandler = ({ target }: ChangeEvent<HTMLInputElement>) => onChange(target.value);

  const buttonProps = buttonRoute
    ? { route: buttonRoute }
    : buttonHref
      ? { href: buttonHref }
      : { htmlType: 'submit' as const };

  return (
    <form
      className={cn(
        inputStyles.form,
        sizeClassMap[size],
        theme === 'dark' && inputStyles.themeDark,
        disabled && inputStyles.isDisabled,
        fullWidth && inputStyles.fullWidth,
        noBorder && inputStyles.noBorder,
        error && inputStyles.hasError,
        className,
      )}
      onSubmit={onFormSubmit}
      noValidate
    >
      <div className={inputStyles.field}>
        {icon && <Icon type={icon} size="md" className={inputStyles.icon} />}
        <input
          type="email"
          data-test={dataTestId}
          aria-label={ariaLabel}
          className={inputStyles.input}
          placeholder={text || placeholder}
          onChange={onChangeHandler}
          disabled={disabled}
          value={value}
          name={name}
        />
      </div>

      <Button
        type={theme === 'dark' ? 'white' : 'dark'}
        size={buttonSizeMap[size]}
        loading={loading}
        disabled={disabled}
        className={inputStyles.button}
        onClick={buttonRoute || buttonHref ? onButtonClick : undefined}
        {...buttonProps}
      >
        {buttonLabel}
      </Button>

      {error && (
        <span className={inputStyles.errorLabel} data-test="input-error-label">
          {Array.isArray(error) ? error.join(', ') : error}
        </span>
      )}
    </form>
  );
};
